import React, { useState, useRef, useEffect } from 'react';
import Header from './Header';
import { useApp } from '../context/AppContext';
import { RetryIcon, CheckIcon, CameraIcon } from './Icons';
import './CameraCapture.css';

interface CameraCaptureProps {
  onBack: () => void;
  onCapture: (imageData: string) => void;
  templateTitle: string;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ 
  onBack,
  onCapture,
  templateTitle
}) => {
  const { photoProcessingMode } = useApp();
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [photoData, setPhotoData] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // 启动摄像头
  const startCamera = React.useCallback(async () => {
    try {
      // 根据照片处理方式确定比例
      let aspectRatio = 1; // 默认1:1比例（正方形）
      let idealWidth = Math.min(window.screen.width, window.screen.height) * window.devicePixelRatio;
      let idealHeight = idealWidth;
      
      if (photoProcessingMode === 'style-only') {
        // 仅风格化模式使用标准照片打印尺寸比例（3:4）
        // aspectRatio = 4 / 6; // 宽:高 = 3:4
        // idealWidth = window.screen.width * window.devicePixelRatio * 0.8;
        // idealHeight = idealWidth * (4 / 3); // 高度 = 宽度 * (4/3)
      }
      
      // 请求摄像头权限并获取视频流
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: idealWidth },
          height: { ideal: idealHeight },
          aspectRatio: { ideal: aspectRatio },
          facingMode: 'user'
        },
        audio: false
      });
      
      // 保存流的引用以便后续清理
      streamRef.current = stream;
      
      // 将视频流绑定到video元素
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // 设置视频元素样式以保持正确比例
        videoRef.current.style.objectFit = 'cover';
      }
    } catch (err) {
      console.error('无法访问摄像头:', err);
      alert('无法访问摄像头，请确保您已授予摄像头权限并刷新页面。');
    }
  }, [photoProcessingMode]);

  // 组件加载时启动摄像头
  useEffect(() => {
    startCamera();
    // 组件卸载时清理摄像头资源
    return () => {
      stopCamera();
    };
  }, [photoProcessingMode, startCamera]); // 加入 startCamera 依赖

  // 停止摄像头
  const stopCamera = () => {
    if (streamRef.current) {
      // 停止所有视频轨道
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    
    // 清除视频元素的srcObject
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // 开始倒计时拍照
  const handleStartCountdown = () => {
    setIsCountingDown(true);
    setCountdown(5);

    // 倒计时逻辑
    const countdownInterval = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdownInterval);
          takePhoto();
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
  };

  // 翻转照片函数（左右镜像翻转）
  const flipImageHorizontally = (imageData: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          // 水平翻转
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(img, 0, 0);
          
          // 转换为图像数据
          const flippedImageData = canvas.toDataURL('image/jpeg', 0.9);
          resolve(flippedImageData);
        } else {
          resolve(imageData);
        }
      };
      img.src = imageData;
    });
  };

  // 拍照
  const takePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // 获取视频的尺寸
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      
      let canvasWidth, canvasHeight, offsetX, offsetY, cropWidth, cropHeight;
      
      if (photoProcessingMode === 'style-only') {
        // 仅风格化模式：使用3:4比例
        const targetAspectRatio = 3 / 4; // 宽:高 = 3:4
        
        if (videoWidth / videoHeight > targetAspectRatio) {
          // 视频比目标比例更宽，以高度为准
          cropHeight = videoHeight;
          cropWidth = videoHeight * targetAspectRatio;
          offsetX = (videoWidth - cropWidth) / 2;
          offsetY = 0;
        } else {
          // 视频比目标比例更高，以宽度为准
          cropWidth = videoWidth;
          cropHeight = videoWidth / targetAspectRatio;
          offsetX = 0;
          offsetY = (videoHeight - cropHeight) / 2;
        }
        
        canvasWidth = cropWidth;
        canvasHeight = cropHeight;
      } else {
        // 光栅合成模式：使用正方形比例
        const size = Math.min(videoWidth, videoHeight);
        
        canvasWidth = size;
        canvasHeight = size;
        cropWidth = size;
        cropHeight = size;
        offsetX = (videoWidth - size) / 2;
        offsetY = (videoHeight - size) / 2;
      }
      
      // 设置canvas尺寸
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      
      // 在canvas上绘制视频当前帧
      const context = canvas.getContext('2d');
      if (context) {
        // 从视频中裁剪指定区域
        context.drawImage(
          video,
          offsetX, offsetY, cropWidth, cropHeight, // 源图像的剪切区域
          0, 0, canvasWidth, canvasHeight // 目标画布区域
        );
        
        // 将canvas内容转换为图像数据
        const imageData = canvas.toDataURL('image/jpeg', 0.9);
        
        // 翻转照片（左右镜像）
        const flippedImageData = await flipImageHorizontally(imageData);

        setPhotoData(flippedImageData);
        setPhotoTaken(true);
        setIsCountingDown(false);
      }
    }
  };

  // 重新拍照
  const handleRetakePhoto = () => {
    setPhotoTaken(false);
    setPhotoData(null);

    startCamera();
  };

  // 确认使用当前照片
  const handleConfirmPhoto = () => {
    if (photoData) {
      // 确保在调用onCapture前停止摄像头
      stopCamera();
      onCapture(photoData);
    }
  };

  // 返回按钮被点击时的处理函数
  const handleBackClick = () => {
    stopCamera();
    onBack();
  };

  return (
    <div className="camera-capture-container">
      <Header 
        onBack={handleBackClick}
        title="拍摄照片"
      />
      
      <div className="template-detail-content camera-capture-content">
        <div className="camera-main-area">
          {!photoTaken ? (
            <div className={`camera-video-container ${photoProcessingMode === 'style-only' ? 'photo-mode' : 'square-mode'}`}>
              <video 
                ref={videoRef}
                autoPlay
                playsInline
                className="camera-video"
              />
              
              {isCountingDown && (
                <div className="countdown-overlay">
                  <div className="countdown-number">{countdown}</div>
                </div>
              )}
            </div>
          ) : (
            <div className={`photo-preview-container ${photoProcessingMode === 'style-only' ? 'photo-mode' : 'square-mode'}`}>
              <img 
                src={photoData || ''} 
                alt="拍摄的照片"
                className="photo-preview" 
              />
            </div>
          )}
          
          {/* 隐藏的canvas用于拍照 */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
        
        <div className="camera-controls">
          {!photoTaken ? (
            <button 
              className="camera-button start-countdown-button"
              onClick={handleStartCountdown}
              disabled={isCountingDown}
            >
              <CameraIcon width="24" height="24" />
              {isCountingDown ? `${countdown}秒后拍照` : '倒数5秒拍照'}
            </button>
          ) : (
            <div className="photo-action-buttons">
              <button 
                className="camera-action-button retake-button"
                onClick={handleRetakePhoto}
              >
                <RetryIcon width="24" height="24" />
                重新拍照
              </button>
              <button 
                className="camera-action-button confirm-button"
                onClick={handleConfirmPhoto}
              >
                <CheckIcon width="24" height="24" />
                使用照片
              </button>
            </div>
          )}

          <div className="camera-instruction">
            <p>请确保您的<strong>头部和身体</strong>完整出现在画面中以获得最佳效果</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
